import React, { useState, useEffect } from 'react';
import {
  fetchRegions,
  fetchCouncils,
  fetchWards,
  fetchStreets,
  fetchRoads,
  fetchHouses,
  fetchHouseInfo,
} from '../services/adminLocationService';
import './StoreLocationPicker.css';

const getName = (item) => item?.name || item?.regionName || item?.councilName || item?.wardName || item?.streetName || item?.roadName;
const getId = (item) => item?.id || item?.regionId || item?.councilId || item?.wardId || item?.streetId || item?.roadId;

const StoreLocationPicker = ({ value, onChange, disabled }) => {
  const [locationType, setLocationType] = useState(value?.surveyed === 'yes' ? 'surveyed' : 'unsurveyed');
  const [locationData, setLocationData] = useState({
    regions: [],
    councils: [],
    wards: [],
    streets: [],
    roads: [],
    houses: [],
  });
  const [selected, setSelected] = useState({
    region: value?.regionId || '',
    district: value?.districtId || '',
    ward: value?.wardId || '',
    street: value?.streetId || '',
    road: value?.roadId || '',
    houseNo: '',
    addressId: null,
  });
  const [mapPreview, setMapPreview] = useState(null);
  const [loading, setLoading] = useState({});
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (value?.surveyed === 'yes' && value?.coordinates && Array.isArray(value.coordinates) && value.coordinates.length >= 2) {
      const [lng, lat] = value.coordinates;
      setMapPreview({
        lat,
        lng,
        fullAddress: [value.road, value.street, value.ward, value.district, value.region].filter(Boolean).join(', '),
      });
    } else if (!selected.houseNo) {
      setMapPreview(null);
    }
  }, [value?.surveyed, value?.coordinates, value?.region, value?.district, value?.ward, value?.street, value?.road]);

  useEffect(() => {
    if (locationType === 'surveyed') {
      setApiError(null);
      fetchRegions()
        .then((regions) => setLocationData((d) => ({ ...d, regions })))
        .catch((err) => setApiError(err.message || 'Failed to load regions'));
    }
  }, [locationType]);

  const loadCouncils = (regionId) => {
    setLoading((l) => ({ ...l, councils: true }));
    fetchCouncils(regionId)
      .then((councils) => {
        setLocationData((d) => ({ ...d, councils }));
        setSelected((s) => ({ ...s, district: '', ward: '', street: '', road: '', houseNo: '', addressId: null }));
      })
      .catch((err) => setApiError(err.message))
      .finally(() => setLoading((l) => ({ ...l, councils: false })));
  };

  const loadWards = (councilId) => {
    setLoading((l) => ({ ...l, wards: true }));
    fetchWards(councilId)
      .then((wards) => {
        setLocationData((d) => ({ ...d, wards }));
        setSelected((s) => ({ ...s, ward: '', street: '', road: '', houseNo: '', addressId: null }));
      })
      .catch((err) => setApiError(err.message))
      .finally(() => setLoading((l) => ({ ...l, wards: false })));
  };

  const loadStreets = (wardId) => {
    setLoading((l) => ({ ...l, streets: true }));
    fetchStreets(wardId)
      .then((streets) => {
        setLocationData((d) => ({ ...d, streets }));
        setSelected((s) => ({ ...s, street: '', road: '', houseNo: '', addressId: null }));
      })
      .catch((err) => setApiError(err.message))
      .finally(() => setLoading((l) => ({ ...l, streets: false })));
  };

  const loadRoads = (streetId) => {
    setLoading((l) => ({ ...l, roads: true }));
    fetchRoads(streetId)
      .then((roads) => {
        setLocationData((d) => ({ ...d, roads }));
        setSelected((s) => ({ ...s, road: '', houseNo: '', addressId: null }));
      })
      .catch((err) => setApiError(err.message))
      .finally(() => setLoading((l) => ({ ...l, roads: false })));
  };

  const loadHouses = (roadId) => {
    setLoading((l) => ({ ...l, houses: true }));
    fetchHouses(roadId)
      .then((houses) => {
        setLocationData((d) => ({ ...d, houses }));
        setSelected((s) => ({ ...s, houseNo: '', addressId: null }));
      })
      .catch((err) => setApiError(err.message))
      .finally(() => setLoading((l) => ({ ...l, houses: false })));
  };

  const handleSelect = (field, val) => {
    setSelected((prev) => {
      const next = { ...prev, [field]: val };
      if (field === 'region') loadCouncils(val);
      else if (field === 'district') loadWards(val);
      else if (field === 'ward') loadStreets(val);
      else if (field === 'street') loadRoads(val);
      else if (field === 'road') loadHouses(val);
      else if (field === 'houseNo') {
        const house = locationData.houses.find((h) => String(h.houseNumber) === String(val) || h.houseNumber === parseInt(val, 10));
        if (house && prev.road) {
          next.addressId = house.addressId;
          // Instant preview from house item coordinates
          const directLat = house.latitude ?? house.lat;
          const directLng = house.longitude ?? house.lng;
          if (directLat != null && directLng != null) {
            setMapPreview({ lat: directLat, lng: directLng, fullAddress: '', addressCode: '', postcode: '' });
          }
          fetchHouseInfo(prev.road, house.addressId)
            .then((info) => {
              const lat = info.latitude ?? info.lat;
              const lng = info.longitude ?? info.lng;
              const addressCode = info.addressCode || '';
              const postcode    = info.postcode    || '';
              const fullAddress = [
                String(house.houseNumber || ''),
                info.road, info.street, info.ward, info.district, info.region, postcode,
              ].filter(Boolean).join(', ');
              setMapPreview({ lat, lng, fullAddress, addressCode, postcode });
              onChange({
                surveyed:       'yes',
                region:         info.region,
                district:       info.district,
                ward:           info.ward,
                street:         info.street,
                road:           info.road,
                buildingNumber: String(house.houseNumber || ''),
                addressId:      String(house.addressId   || ''),
                addressCode,
                postcode,
                coordinates:    [lng, lat],
              });
            })
            .catch((err) => setApiError(err.message));
        }
      }
      return next;
    });
  };

  const handleTypeChange = (e) => {
    const t = e.target.value;
    setLocationType(t);
    setMapPreview(null);
    setApiError(null);
    if (t === 'unsurveyed') {
      onChange({ surveyed: 'no', unsurveyedAddress: value?.unsurveyedAddress || '', coordinates: null });
    } else {
      onChange({ surveyed: 'yes', region: '', district: '', ward: '', street: '', road: '', buildingNumber: '', coordinates: null });
    }
  };

  const handleUnsurveyedChange = (e) => {
    onChange({ surveyed: 'no', unsurveyedAddress: e.target.value, coordinates: null });
  };

  return (
    <div className="store-location-picker">
      <div className="store-location-picker-field">
        <label>Location Type</label>
        <select value={locationType} onChange={handleTypeChange} disabled={disabled}>
          <option value="">Select...</option>
          <option value="surveyed">Surveyed (Use official location database)</option>
          <option value="unsurveyed">Unsurveyed (Manual address)</option>
        </select>
      </div>

      {apiError && (
        <div className="store-location-picker-error">
          {apiError}. Ensure REACT_APP_LOCATION_API_URL is set if using a custom API.
        </div>
      )}

      {locationType === 'unsurveyed' && (
        <div className="store-location-picker-field">
          <label>Full Address</label>
          <textarea
            value={value?.unsurveyedAddress || ''}
            onChange={handleUnsurveyedChange}
            placeholder="Enter complete store address"
            rows={3}
            disabled={disabled}
          />
        </div>
      )}

      {locationType === 'surveyed' && (
        <>
          <div className="store-location-picker-cascade">
            <div className="store-location-picker-field">
              <label>Region</label>
              <select
                value={selected.region}
                onChange={(e) => handleSelect('region', e.target.value)}
                disabled={disabled || loading.regions}
              >
                <option value="">Select Region</option>
                {locationData.regions.map((r) => (
                  <option key={getId(r)} value={getId(r)}>{getName(r)}</option>
                ))}
              </select>
            </div>
            <div className="store-location-picker-field">
              <label>District</label>
              <select
                value={selected.district}
                onChange={(e) => handleSelect('district', e.target.value)}
                disabled={disabled || !selected.region || loading.councils}
              >
                <option value="">Select District</option>
                {locationData.councils.map((c) => (
                  <option key={getId(c)} value={getId(c)}>{getName(c)}</option>
                ))}
              </select>
            </div>
            <div className="store-location-picker-field">
              <label>Ward</label>
              <select
                value={selected.ward}
                onChange={(e) => handleSelect('ward', e.target.value)}
                disabled={disabled || !selected.district || loading.wards}
              >
                <option value="">Select Ward</option>
                {locationData.wards.map((w) => (
                  <option key={getId(w)} value={getId(w)}>{getName(w)}</option>
                ))}
              </select>
            </div>
            <div className="store-location-picker-field">
              <label>Street</label>
              <select
                value={selected.street}
                onChange={(e) => handleSelect('street', e.target.value)}
                disabled={disabled || !selected.ward || loading.streets}
              >
                <option value="">Select Street</option>
                {locationData.streets.map((s) => (
                  <option key={getId(s)} value={getId(s)}>{getName(s)}</option>
                ))}
              </select>
            </div>
            <div className="store-location-picker-field">
              <label>Road</label>
              <select
                value={selected.road}
                onChange={(e) => handleSelect('road', e.target.value)}
                disabled={disabled || !selected.street || loading.roads}
              >
                <option value="">Select Road</option>
                {locationData.roads.map((r) => (
                  <option key={getId(r)} value={getId(r)}>{getName(r)}</option>
                ))}
              </select>
            </div>
            <div className="store-location-picker-field">
              <label>House Number</label>
              <select
                value={selected.houseNo}
                onChange={(e) => handleSelect('houseNo', e.target.value)}
                disabled={disabled || !selected.road || loading.houses}
              >
                <option value="">Select House</option>
                {locationData.houses.map((h) => (
                  <option key={h.addressId} value={h.houseNumber}>{h.houseNumber}</option>
                ))}
              </select>
            </div>
          </div>
          {mapPreview && mapPreview.lat != null && (
            <div className="store-location-picker-map">
              <label>Location Preview</label>
              <div className="store-location-picker-map-frame">
                <iframe
                  title="Store location"
                  src={`https://www.google.com/maps?q=${mapPreview.lat},${mapPreview.lng}&z=18&t=k&output=embed`}
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              {mapPreview.fullAddress && (
                <p className="store-location-picker-address">{mapPreview.fullAddress}</p>
              )}
              <div className="store-location-picker-meta">
                {mapPreview.addressCode && (
                  <span className="store-location-picker-code">🏷 {mapPreview.addressCode}</span>
                )}
                {mapPreview.postcode && (
                  <span className="store-location-picker-postcode">📮 {mapPreview.postcode}</span>
                )}
                <span className="store-location-picker-coords">
                  📍 {Number(mapPreview.lat).toFixed(6)}, {Number(mapPreview.lng).toFixed(6)}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StoreLocationPicker;
